import { useEffect, useState } from 'react';
import { fetchCourses } from '@/services/learn/course.service';
import { Course, CourseEntity, LearnPage } from '@/graphql/generated/graphql';
import CoursesSkeleton from './Courses.skeleton';
import { getAuthors } from '@/utils/learn';
import Container from '@/components/Container';
import Navigation, { NavigationSkeleton } from '@/components/ui/navigation';
import { navigationLearn } from '../constants';
import { PlayIcon } from '@heroicons/react/24/solid';
import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import { joinWithCommaAnd } from '@/utils/array';
import Button from '@/components/Button';
import { useNavigate } from 'react-router-dom';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import Card from '@/components/Card';
import WelcomePageMessage from '@/components/WelcomePage';
import { getPage } from '@/services/learn/learn.service';

function CoursesTab() {
  const [courses, setCourses] = useState<Array<CourseEntity> | null>(null);
  const [learnPage, setLearnPage] = useState<LearnPage | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);

      const coursesResult = await fetchCourses();
      const pageResponse = await getPage();

      setCourses(coursesResult);
      setLearnPage(pageResponse.page);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <>
        <NavigationSkeleton />
        <Container className="min-h-40 w-full">
          <CoursesSkeleton />
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation title="Learn" items={navigationLearn} />
      <WelcomePageMessage
        component={learnPage?.welcomePageMessage}
        hideDivider
        className="lg:col-start-3 lg:col-end-11"
      />
      <Container className="last-divider-hidden mt-sh-60 min-h-40">
        <div className="col-span-12 last:mb-0 lg:col-start-3 lg:col-end-11">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {courses?.map((c) => {
              const course = c?.attributes;
              const cardProps = {
                title: course?.title || '',
                tags: [
                  <DefaultContentTemplateTags
                    key={course?.title}
                    duration={course?.duration || ''}
                    tags={['Course']}
                    className="!mb-sh-15 !mt-0"
                  />,
                ],
                description: course?.description || '',
                author: joinWithCommaAnd(getAuthors(course as Course)?.map((author) => author?.attributes?.name)),
                image: {
                  url: course!.thumbnail.data?.attributes?.url || '',
                  alternativeText: course!.thumbnail.data?.attributes?.alternativeText || '',
                },
                button: (
                  <Button
                    theme="solid"
                    onClick={() => navigate(`/learn/course/${course?.slug}`)}
                    className="col-span-12 !mt-0">
                    <PlayIcon className="inline-block h-4 w-4" />
                    <span>Find out more</span>
                  </Button>
                ),
              };

              return (
                <AnimatedFadeInComponent key={course!.title!}>
                  <Card {...cardProps} key={course!.title!} />
                </AnimatedFadeInComponent>
              );
            })}
          </div>
        </div>
      </Container>
    </>
  );
}

export default CoursesTab;
